import { useMemo } from "react";
import type { InfoScreenImage, InfoScreenPage, InfoScreenTextBlock } from "@/types/infoScreen";

type Props = {
  page: InfoScreenPage;
  slideIndex: number;
};

function sortedBlocks(blocks: InfoScreenTextBlock[]): InfoScreenTextBlock[] {
  return [...blocks].sort((a, b) => a.sort_order - b.sort_order);
}

function sortedImages(images: InfoScreenImage[]): InfoScreenImage[] {
  return [...images].sort((a, b) => a.sort_order - b.sort_order);
}

function pickSlideImage(images: InfoScreenImage[], slideIndex: number): InfoScreenImage | null {
  if (images.length === 0) return null;
  const idx = ((slideIndex % images.length) + images.length) % images.length;
  return images[idx] ?? null;
}

/** Nutzt dieselben Klassen wie Split/Centered — damit Design-Themes greifen. */
function ColumnTextBlock({ block }: { block: InfoScreenTextBlock }) {
  return (
    <article className={`guest-tablet-idle-text guest-tablet-idle-text--${block.variant}`}>
      {block.title ? <h2 className="guest-tablet-idle-text-title">{block.title}</h2> : null}
      <p className="guest-tablet-idle-text-body whitespace-pre-line">{block.body}</p>
    </article>
  );
}

function ColumnImage({
  image,
  slideCount,
  slideIndex,
}: {
  image: InfoScreenImage;
  slideCount: number;
  slideIndex: number;
}) {
  return (
    <figure className="info-screen-columns-media guest-tablet-idle-slideshow">
      <div className="info-screen-columns-media__frame">
        <img src={image.image_url} alt={image.alt_text || ""} />
      </div>
      {image.caption ? (
        <figcaption className="info-screen-columns-media__caption guest-tablet-idle-slide-caption">
          {image.caption}
        </figcaption>
      ) : null}
      {slideCount > 1 ? (
        <div className="info-screen-columns-media__dots guest-tablet-idle-dots" aria-hidden>
          {Array.from({ length: slideCount }, (_, i) => (
            <span
              key={i}
              className={
                i === slideIndex % slideCount
                  ? "guest-tablet-idle-dot guest-tablet-idle-dot--active"
                  : "guest-tablet-idle-dot"
              }
            />
          ))}
        </div>
      ) : null}
    </figure>
  );
}

function ColumnPanel({
  side,
  blocks,
  images,
  slideIndex,
}: {
  side: "left" | "right";
  blocks: InfoScreenTextBlock[];
  images: InfoScreenImage[];
  slideIndex: number;
}) {
  const activeImage = pickSlideImage(images, slideIndex);
  const hasContent = blocks.length > 0 || images.length > 0;

  if (!hasContent) {
    return (
      <div className={`info-screen-columns-panel info-screen-columns-panel--${side}`}>
        <div className="info-screen-columns-panel__empty" aria-hidden />
      </div>
    );
  }

  return (
    <div className={`info-screen-columns-panel info-screen-columns-panel--${side}`}>
      {activeImage ? (
        <ColumnImage image={activeImage} slideCount={images.length} slideIndex={slideIndex} />
      ) : null}
      {blocks.length > 0 ? (
        <div className="info-screen-columns-panel__content guest-tablet-idle-content">
          {blocks.map((block) => (
            <ColumnTextBlock key={block.id} block={block} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function HeroBanner({ images, slideIndex }: { images: InfoScreenImage[]; slideIndex: number }) {
  const active = pickSlideImage(images, slideIndex);
  if (!active) return null;

  return (
    <figure className="info-screen-columns-hero guest-tablet-idle-slideshow">
      <img src={active.image_url} alt={active.alt_text || ""} />
      {active.caption ? (
        <figcaption className="info-screen-columns-hero__caption guest-tablet-idle-slide-caption">
          {active.caption}
        </figcaption>
      ) : null}
      {images.length > 1 ? (
        <div className="info-screen-columns-hero__dots guest-tablet-idle-dots" aria-hidden>
          {images.map((img, i) => (
            <span
              key={img.id}
              className={
                i === slideIndex % images.length
                  ? "guest-tablet-idle-dot guest-tablet-idle-dot--active"
                  : "guest-tablet-idle-dot"
              }
            />
          ))}
        </div>
      ) : null}
    </figure>
  );
}

export function InfoScreenColumnsLayout({ page, slideIndex }: Props) {
  const { heroImages, leftImages, rightImages } = useMemo(() => {
    const all = sortedImages(page.images);
    const hero: InfoScreenImage[] = [];
    const left: InfoScreenImage[] = [];
    const right: InfoScreenImage[] = [];

    for (const img of all) {
      if (img.column === "left") left.push(img);
      else if (img.column === "right") right.push(img);
      else hero.push(img);
    }

    return { heroImages: hero, leftImages: left, rightImages: right };
  }, [page.images]);

  const leftBlocks = sortedBlocks(page.column_left_blocks);
  const rightBlocks = sortedBlocks(page.column_right_blocks);

  return (
    <div className="info-screen-page--columns" aria-live="polite">
      {heroImages.length > 0 ? <HeroBanner images={heroImages} slideIndex={slideIndex} /> : null}
      <div className="info-screen-columns-layout">
        <ColumnPanel
          side="left"
          blocks={leftBlocks}
          images={leftImages}
          slideIndex={slideIndex}
        />
        <div className="info-screen-columns-layout__divider" aria-hidden />
        <ColumnPanel
          side="right"
          blocks={rightBlocks}
          images={rightImages}
          slideIndex={slideIndex}
        />
      </div>
    </div>
  );
}
